import React, { useState, useEffect } from 'react';
import {
  FiSearch,
  FiSmartphone,
  FiX,
  FiUsers,
  FiRefreshCw,
  FiEye,
  FiEyeOff,
  FiKey,
  FiChevronLeft,
  FiChevronRight,
  FiEdit,
  FiSave
} from 'react-icons/fi';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const AppUserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({ fullName: '', city: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    fetchUsers();
  }, [search, currentPage, itemsPerPage]);

  const fetchUsers = async (isManualRefresh = false) => {
    try {
      if (isManualRefresh) {
        setRefreshing(true);
        toast.loading('Refreshing app users...', { id: 'refresh' });
      } else {
        setLoading(true);
      }

      const params = {
        page: currentPage,
        limit: itemsPerPage,
        ...(search && { search })
      };

      console.log('Fetching app users with params:', params);
      const response = await api.get('/superadmin/app-users', { params });
      console.log('App users response:', response.data);

      if (response?.data?.success) {
        setUsers(response.data.data || []);
        setTotalPages(response.data.totalPages || 1);
        setTotalUsers(response.data.total || 0);
        
        // Show success message for manual refresh
        if (isManualRefresh) {
          toast.success(`Refreshed! Found ${response.data.data?.length || 0} users`, { id: 'refresh' });
        }
      } else {
        toast.error('Failed to load app users data', { id: 'refresh' });
      }
    } catch (error) {
      console.error('Fetch app users error:', error);
      toast.error(`Failed to fetch app users: ${error.response?.data?.message || error.message}`, { id: 'refresh' });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      const response = await api.put(
        `/superadmin/app-users/${selectedUser._id}/reset-password`,
        { newPassword }
      );

      if (response?.data?.success) {
        toast.success('Password reset successfully');
        setShowPasswordModal(false);
        setNewPassword('');
        setSelectedUser(null);
      }
    } catch {
      toast.error('Failed to reset password');
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setEditFormData({
      fullName: user.fullName || user.name || '',
      city: user.city || ''
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async () => {
    if (!editFormData.fullName.trim() && !editFormData.city.trim()) {
      toast.error('Please provide at least name or city to update');
      return;
    }

    try {
      const response = await api.put(
        `/superadmin/app-users/${selectedUser._id}`,
        editFormData
      );

      if (response?.data?.success) {
        toast.success('User updated successfully');
        setShowEditModal(false);
        setSelectedUser(null);
        setEditFormData({ fullName: '', city: '' });
        fetchUsers();
      }
    } catch (error) {
      toast.error(`Failed to update user: ${error.response?.data?.message || error.message}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="relative inline-flex items-center justify-center">
            <div className="w-20 h-20 border-4 border-indigo-200 rounded-full"></div>
            <div className="w-20 h-20 border-4 border-indigo-600 rounded-full animate-spin absolute border-t-transparent"></div>
          </div>
          <p className="mt-4 text-gray-600 font-medium">
            Loading App Users...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-6 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
                <FiUsers className="text-white text-xl" />
              </div>
              App User Management
            </h1>
            <p className="text-gray-600 mt-1">
              View and manage all app users
            </p>
          </div>

          <div className="flex items-center gap-3">
            <div className="px-4 py-2 bg-green-100 text-green-800 rounded-full text-sm font-semibold flex items-center gap-2">
              <FiUsers />
              {totalUsers} Users
            </div>

            <button
              onClick={() => fetchUsers(true)}
              disabled={refreshing}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              <FiRefreshCw className={`text-sm ${refreshing ? 'animate-spin' : ''}`} />
              <span className="text-sm font-medium">{refreshing ? 'Refreshing...' : 'Refresh'}</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Search */}
        <div className="bg-white rounded-xl shadow-md border p-6 mb-8">
          <div className="flex gap-4 items-center">
            <div className="flex-1 relative">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Search by name, mobile number, or email..."
                className="w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm border overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                {[
                  'Name',
                  'Mobile',
                  'City',
                  'State',
                  'Device',
                  'Joined',
                  'Actions'
                ].map((h) => (
                  <th
                    key={h}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className="divide-y">
              {users.map((user) => (
                <tr key={user._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-semibold">
                    {user.fullName || user.name || 'N/A'}
                  </td>
                  <td className="px-6 py-4">
                    <code className="px-2 py-1 bg-gray-100 rounded">
                      {user.mobileNumber}
                    </code>
                  </td>
                  <td className="px-6 py-4">{user?.city || 'N/A'}</td>
                  <td className="px-6 py-4">{user?.state || 'N/A'}</td>
                  <td className="px-6 py-4 flex items-center gap-2">
                    <FiSmartphone />
                    {user?.deviceInfo?.deviceType || 'Unknown'}
                  </td>
                  <td className="px-6 py-4">
                    {new Date(user.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => handleEditUser(user)}
                      className="inline-flex items-center gap-2 px-3 py-1.5 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 transition-colors duration-200"
                    >
                      <FiEdit className="text-xs" />
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {users.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              No app users found
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white rounded-xl shadow-sm border p-6 mt-8 flex justify-between items-center">
            <button
              onClick={() =>
                setCurrentPage((p) => Math.max(1, p - 1))
              }
              disabled={currentPage === 1}
              className="group px-4 py-2 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-lg disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed transition-all duration-200 shadow-md hover:shadow-lg flex items-center gap-2"
            >
              <FiChevronLeft className="group-hover:-translate-x-0.5 transition-transform" />
              Previous
            </button>

            <span className="text-gray-600 font-medium">
              Page {currentPage} of {totalPages}
            </span>

            <button
              onClick={() =>
                setCurrentPage((p) => Math.min(totalPages, p + 1))
              }
              disabled={currentPage === totalPages}
              className="group px-4 py-2 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-lg disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed transition-all duration-200 shadow-md hover:shadow-lg flex items-center gap-2"
            >
              Next
              <FiChevronRight className="group-hover:translate-x-0.5 transition-transform" />
            </button>
          </div>
        )}
      </div>

      {/* Password Modal */}
      {showPasswordModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold mb-4">
              Reset User Password
            </h2>

            <div className="relative mb-4">
              <input
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="New password"
                className="w-full px-4 py-2 border rounded-lg pr-12"
              />
              <button
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2"
              >
                {showPassword ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowPasswordModal(false)}
                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-lg font-medium hover:from-gray-600 hover:to-gray-700 transition-all duration-200 shadow-md hover:shadow-lg"
              >
                Cancel
              </button>
              <button
                onClick={handlePasswordReset}
                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg font-medium hover:from-purple-600 hover:to-purple-700 transition-all duration-200 shadow-md hover:shadow-lg flex items-center justify-center gap-2"
              >
                <FiKey className="group-hover:rotate-12 transition-transform" />
                Reset Password
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold mb-4">
              Edit User Details
            </h2>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  value={editFormData.fullName}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, fullName: e.target.value }))}
                  placeholder="Enter user name"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  City
                </label>
                <input
                  type="text"
                  value={editFormData.city}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, city: e.target.value }))}
                  placeholder="Enter city"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedUser(null);
                  setEditFormData({ fullName: '', city: '' });
                }}
                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-lg font-medium hover:from-gray-600 hover:to-gray-700 transition-all duration-200 shadow-md hover:shadow-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleEditSubmit}
                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-indigo-500 to-indigo-600 text-white rounded-lg font-medium hover:from-indigo-600 hover:to-indigo-700 transition-all duration-200 shadow-md hover:shadow-lg flex items-center justify-center gap-2"
              >
                <FiSave className="group-hover:scale-110 transition-transform" />
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppUserManagement;
